import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuditReport, Discrepancy } from '../hooks/useAuditReport';

function formatAud(value: string): string {
  const num = parseFloat(value);
  if (isNaN(num)) return '$0.00';
  return `$${num.toFixed(2)}`;
}

function LoadingSpinner() {
  return (
    <div className="audit-report-loading" role="status" aria-live="polite">
      <div className="spinner" aria-hidden="true" />
      <p>Generating your audit report...</p>
    </div>
  );
}

function ErrorDisplay({ message }: { message: string }) {
  const navigate = useNavigate();
  return (
    <div className="audit-report-error" role="alert" aria-live="assertive">
      <h2>Error</h2>
      <p>{message}</p>
      <button onClick={() => navigate('/dashboard')} className="back-button">
        Back to Dashboard
      </button>
    </div>
  );
}

function ReportSummary({
  totalExpectedPay,
  totalActualPay,
  totalDiscrepancy,
}: {
  totalExpectedPay: string;
  totalActualPay: string;
  totalDiscrepancy: string;
}) {
  return (
    <section className="report-summary" aria-labelledby="summary-heading">
      <h2 id="summary-heading">Pay Audit Summary</h2>
      <dl className="summary-grid">
        <div className="summary-item">
          <dt>Total Expected Pay</dt>
          <dd>{formatAud(totalExpectedPay)}</dd>
        </div>
        <div className="summary-item">
          <dt>Total Actual Pay</dt>
          <dd>{formatAud(totalActualPay)}</dd>
        </div>
        <div className="summary-item summary-item-discrepancy">
          <dt>Total Discrepancy</dt>
          <dd>{formatAud(totalDiscrepancy)}</dd>
        </div>
      </dl>
    </section>
  );
}

function DiscrepancyTable({ discrepancies }: { discrepancies: Discrepancy[] }) {
  const sorted = [...discrepancies].sort(
    (a, b) => new Date(a.shiftDate).getTime() - new Date(b.shiftDate).getTime()
  );

  return (
    <section className="discrepancy-section" aria-labelledby="discrepancy-heading">
      <h2 id="discrepancy-heading">Discrepancies Found</h2>
      <table className="discrepancy-table" aria-label="Pay discrepancies by shift">
        <thead>
          <tr>
            <th scope="col">Shift Date</th>
            <th scope="col">Expected</th>
            <th scope="col">Actual</th>
            <th scope="col">Difference</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((d, index) => (
            <tr key={`${d.shiftDate}-${index}`} className="discrepancy-row">
              <td>{new Date(d.shiftDate).toLocaleDateString('en-AU')}</td>
              <td>{formatAud(d.expectedAmount)}</td>
              <td>{formatAud(d.actualAmount)}</td>
              <td>{formatAud(d.difference)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function NoDiscrepanciesMessage() {
  return (
    <section className="no-discrepancies" role="status" aria-live="polite">
      <div className="success-message">
        <p>All shifts have been paid correctly</p>
      </div>
    </section>
  );
}

export function AuditReport() {
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();
  const { report, status, error } = useAuditReport(auditId);

  if (status === 'loading') {
    return (
      <main className="audit-report-page" role="main" aria-labelledby="report-page-heading">
        <h1 id="report-page-heading" className="visually-hidden">Audit Report</h1>
        <LoadingSpinner />
      </main>
    );
  }

  if (status === 'error' || !report) {
    return (
      <main className="audit-report-page" role="main" aria-labelledby="report-page-heading">
        <h1 id="report-page-heading" className="visually-hidden">Audit Report</h1>
        <ErrorDisplay message={error ?? 'Failed to load report'} />
      </main>
    );
  }

  return (
    <main className="audit-report-page" role="main" aria-labelledby="report-page-heading">
      <header className="report-header">
        <h1 id="report-page-heading">Audit Report</h1>
        <p className="report-period">
          Pay period: {new Date(report.periodStart).toLocaleDateString('en-AU')} &ndash;{' '}
          {new Date(report.periodEnd).toLocaleDateString('en-AU')}
        </p>
      </header>

      <ReportSummary
        totalExpectedPay={report.totalExpectedPay}
        totalActualPay={report.totalActualPay}
        totalDiscrepancy={report.totalDiscrepancy}
      />

      {report.hasDiscrepancies ? (
        <DiscrepancyTable discrepancies={report.discrepancies} />
      ) : (
        <NoDiscrepanciesMessage />
      )}

      <nav className="report-actions" aria-label="Report actions">
        <button onClick={() => navigate('/dashboard')} className="back-button">
          Back to Dashboard
        </button>
      </nav>
    </main>
  );
}
